import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ContentLoader,
  DirtyFormAlert,
  Label,
  Notice,
  NumberInput,
  Selector,
  SlidePane,
} from 'shared/components';
import {
  mapBundlesToSelector,
  formatPolicies,
  getSectionListData,
} from 'shared/helpers';
import { authMethods } from 'shared/constants';
import { validateRequiredNumber } from 'shared/validation';
import { normalizeProductListItem } from 'CreateOrderForm/helpers';
import { getUserLicenseTemplates, getUserProducts } from 'redux/user/actions';
import { getProductsInBundle } from '../../actions';
import PolicySelector from './PolicySelector';
import './styles.scss';

// Stable fallback so the derived lists keep their identity between renders;
// a new [] each render re-creates the bundle fetch callback and refetches in a loop.
const EMPTY_LIST = [];

const SelectBundleForm = ({
  closeCb,
  handleSubmit,
  isOpen,
  bundleToEdit,
  title,
  width,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isBundleChanged = useRef(false);
  const userID = useSelector((state) => get(state, 'user.details.id'));
  const productsSectionState = useSelector((state) =>
    userID ? get(state, `user.userSections.${userID}.products`, {}) : {},
  );
  const licenseTemplatesSectionState = useSelector((state) =>
    userID
      ? get(state, `user.userSections.${userID}.licenseTemplates`, {})
      : {},
  );
  const legacyProducts = useSelector((state) =>
    get(state, 'user.details.products', EMPTY_LIST),
  );
  const legacyUserPolicies = useSelector((state) =>
    get(state, 'user.details.license_templates', EMPTY_LIST),
  );

  useEffect(() => {
    if (!userID) {
      return;
    }

    dispatch(getUserProducts(userID));
    dispatch(getUserLicenseTemplates(userID));
  }, [dispatch, userID]);

  const productsForbidden = !!get(productsSectionState, 'forbidden');
  const licenseTemplatesForbidden = !!get(
    licenseTemplatesSectionState,
    'forbidden',
  );
  const isSectionForbidden = productsForbidden || licenseTemplatesForbidden;

  const productsSectionData = getSectionListData(
    get(productsSectionState, 'data'),
  );
  const hasProductsSectionData =
    Array.isArray(get(productsSectionState, 'data')) ||
    Array.isArray(get(get(productsSectionState, 'data'), 'results'));
  const productsListState = productsForbidden
    ? EMPTY_LIST
    : hasProductsSectionData
      ? productsSectionData
      : legacyProducts;

  const products = useMemo(
    () =>
      productsListState.map((productItem) => {
        const normalizedProduct = normalizeProductListItem(productItem);

        return {
          product: normalizedProduct.product,
          num_of_license: normalizedProduct.numOfLicense,
        };
      }),
    [productsListState],
  );

  const licenseTemplatesSectionData = getSectionListData(
    get(licenseTemplatesSectionState, 'data'),
  );
  const hasLicenseTemplatesSectionData =
    Array.isArray(get(licenseTemplatesSectionState, 'data')) ||
    Array.isArray(get(get(licenseTemplatesSectionState, 'data'), 'results'));

  const userPolicies = licenseTemplatesForbidden
    ? EMPTY_LIST
    : hasLicenseTemplatesSectionData
      ? licenseTemplatesSectionData
      : legacyUserPolicies;

  const isSectionLoading =
    (get(productsSectionState, 'loading') ||
      get(licenseTemplatesSectionState, 'loading')) &&
    !products.length &&
    !userPolicies.length;

  const bundles = products.filter((p) => p.product.is_bundle);
  const bundleOptions = mapBundlesToSelector(bundles);

  const initialSelectedPolicies = bundleToEdit
    ? get(bundleToEdit, 'selected_bundle_policies')
    : [];

  const [isDirty, setDirty] = useState(false);
  const [isDirtyFormDisplayed, setDirtyFormDisplay] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState(
    get(bundleToEdit, 'bundle', null),
  );
  const [productsInBundleLoading, setProductsInBundleLoading] = useState(true);
  const [bundleProductError, setBundleProductError] = useState(false);
  const [productsInBundle, setProductsInBundle] = useState([]);
  const [selectedProductPolicies, setSelectedProductPolicies] = useState(
    initialSelectedPolicies,
  );
  const [maxLicenseUsers, setMaxLicenseUsers] = useState(
    get(bundleToEdit, 'max_license_users') || 1,
  );
  const [maxLicenseUsersError, setMaxLicenseUsersError] = useState('');
  const isUserBasedBundle =
    get(selectedBundle, 'data.product.authorization_method') ===
    authMethods.user;

  useEffect(() => {
    if (selectedBundle || !bundleOptions.length) {
      return;
    }

    setSelectedBundle(bundleOptions[0]);
  }, [selectedBundle, bundleOptions]);

  const selectedBundleID = get(selectedBundle, 'value');
  const selectedBundleActive = get(selectedBundle, 'data.product.active');
  const selectedBundleArchived = get(
    selectedBundle,
    'data.product.is_archived',
  );
  const isSelectedBundleUnavailable =
    selectedBundleActive === false || selectedBundleArchived === true;
  const selectedBundleUnavailableMessage = [
    selectedBundleArchived ? t('This bundle is archived.') : null,
    selectedBundleActive === false ? t('This bundle is disabled.') : null,
  ]
    .filter(Boolean)
    .join(' ');

  const handlePolicySelect = (policy) => {
    const policyProductID = get(policy, 'product.id');
    const filtered = selectedProductPolicies.filter(
      (spp) => get(spp, 'product.id') !== policyProductID,
    );
    setSelectedProductPolicies([...filtered, policy]);
  };

  const getBundledProducts = useCallback(() => {
    if (isSectionForbidden) {
      setProductsInBundle([]);
      setProductsInBundleLoading(false);
      return;
    }

    if (!selectedBundleID) {
      setProductsInBundle([]);
      setProductsInBundleLoading(false);
      return;
    }

    const setInitialState = (items = []) => {
      const productsList = [];
      const policiesList = [];
      let isMissing = false;
      setBundleProductError(false);
      items.forEach((i) => {
        const itemProductID = get(i, 'id');
        const prWrap = products.find(
          (p) => get(p, 'product.id') === itemProductID,
        );
        const pr = get(prWrap, 'product');
        const policies = userPolicies.filter(
          (up) => get(up, 'product.id') === itemProductID,
        );
        if (!pr || !policies.length) {
          // FOR BUNDLE PRODUCTS LICENSE MANAGER HAS TO HAVE AT LEAST ONE POLICY PER PRODUCT IN BUNDLE
          isMissing = true;
          productsList.push(i);
        } else {
          const mappedPolicies = formatPolicies(policies);
          const prWithPolicies = {
            ...pr,
            license_templates: mappedPolicies,
          };
          productsList.push(prWithPolicies);
          policiesList.push(mappedPolicies[0]);
        }
      });
      setBundleProductError(isMissing);
      setProductsInBundle(productsList);
      if (!bundleToEdit || isBundleChanged.current) {
        setSelectedProductPolicies(policiesList);
      }
      setProductsInBundleLoading(false);
    };

    setProductsInBundleLoading(true);
    getProductsInBundle(selectedBundleID)
      .then((res) => {
        const data = get(res, 'data', []);
        setInitialState(data);
      })
      .catch(() => {
        setProductsInBundleLoading(false);
      });
  }, [
    selectedBundleID,
    products,
    userPolicies,
    bundleToEdit,
    isSectionForbidden,
  ]);

  useEffect(() => {
    getBundledProducts();
  }, [getBundledProducts]);

  const handleBundleSelect = (val) => {
    const selectedB = bundleOptions.find(
      (b) => Number(get(b, 'value')) === Number(val),
    );
    if (!selectedB) {
      return;
    }
    setDirty(true);
    isBundleChanged.current = true;
    setSelectedBundle(selectedB);
  };

  const handleMaxLicenseUsersChange = (val) => {
    setDirty(true);
    setMaxLicenseUsers(val);
    validateRequiredNumber(val).then((err) => setMaxLicenseUsersError(t(err)));
  };

  const handleBundleSubmit = () => {
    handleSubmit({
      bundle: selectedBundle,
      selected_bundle_policies: selectedProductPolicies,
      max_license_users: isUserBasedBundle
        ? Number(maxLicenseUsers)
        : undefined,
    });
  };

  const handleClose = () => {
    if (isDirty) {
      setDirtyFormDisplay(true);
    } else {
      closeCb();
    }
  };

  const getTitle = () => (
    <div className="SelectBundleFormTitle">
      <div className="title">{title}</div>
      <div className="confirm-btn">
        <Button
          theme="success"
          onClick={handleBundleSubmit}
          disabled={
            isSectionForbidden ||
            isSectionLoading ||
            productsInBundleLoading ||
            !productsInBundle.length ||
            bundleProductError ||
            isSelectedBundleUnavailable ||
            (isUserBasedBundle &&
              (!!maxLicenseUsersError || Number(maxLicenseUsers) < 1))
          }
        >
          {t('Confirm')}
        </Button>
      </div>
    </div>
  );

  const isBundleProductCreated =
    bundles && Array.isArray(bundles) && bundles.length > 0;

  return (
    <SlidePane
      closeCb={handleClose}
      isOpen={isOpen}
      title={getTitle()}
      width={width}
    >
      <div className="SelectBundleForm">
        {isSectionForbidden && (
          <Notice theme="error" title={t('Missing permission')}>
            {t(
              'You do not have permission to access products or license templates.',
            )}
          </Notice>
        )}
        {isSectionLoading && !isSectionForbidden && (
          <div className="bundle-products-loading">
            <ContentLoader />
          </div>
        )}
        {!isSectionForbidden && !isSectionLoading && (
          <>
            <div className="section-row">
              <Label inputId="bundle-select" text={t('Bundle name')} />
              <div className="bundle-selector-with-status">
                <Selector
                  options={bundleOptions}
                  handleChange={handleBundleSelect}
                  value={get(selectedBundle, 'value')}
                  inputId="bundle-select"
                  disabled={productsInBundleLoading}
                />
                {(selectedBundleActive === false ||
                  selectedBundleArchived === true) && (
                  <div className="bundle-status-tags">
                    {selectedBundleActive === false && (
                      <div className="BundleStatusTag BundleStatusTag-disabled">
                        {t('Disabled')}
                      </div>
                    )}
                    {selectedBundleArchived === true && (
                      <div className="BundleStatusTag BundleStatusTag-archived">
                        {t('Archived')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {isUserBasedBundle && (
              <div className="section-row">
                <Label
                  inputId="bundle-max-license-users"
                  text={t('Max license users')}
                />
                <NumberInput
                  id="bundle-max-license-users"
                  handleChange={handleMaxLicenseUsersChange}
                  value={maxLicenseUsers}
                  error={maxLicenseUsersError}
                  min="1"
                  max="2147483647"
                />
              </div>
            )}
            {isSelectedBundleUnavailable && (
              <Notice
                theme="error"
                title={t('Selected bundle cannot be used for order creation')}
              >
                {selectedBundleUnavailableMessage}
              </Notice>
            )}
            {!isBundleProductCreated && (
              <Notice
                theme="error"
                size="sm"
                title={t('Bundle products are not defined')}
              >
                <div className="bundle-prod-warning">
                  <span>
                    {t('Configure product bundle before issuing licenses')}
                  </span>
                </div>
              </Notice>
            )}
            <div className="section-row">
              {productsInBundleLoading ? (
                <div className="bundle-products-loading">
                  <ContentLoader />
                </div>
              ) : (
                <div className="bundle-products-list">
                  <div className="bundle-products-heading">
                    {t('Products in a bundle')}
                  </div>
                  {bundleProductError ? (
                    <Notice
                      theme="error"
                      title={t(
                        'Missing bundle products or license policies in your license manager permissions.',
                      )}
                    >
                      <div>
                        <div>{`${t(
                          'Products defined in selected bundle are:',
                        )} ${productsInBundle
                          .map((p) => p.product_name)
                          .join(', ')}.`}</div>
                        <div>
                          {t(
                            'Make sure that those products and at least one license policy per that product is enabled in your license manager permissions.',
                          )}
                        </div>
                      </div>
                    </Notice>
                  ) : (
                    productsInBundle.map((p) => (
                      <PolicySelector
                        key={p.id}
                        product={p}
                        selectedPolicies={selectedProductPolicies}
                        handlePolicySelect={handlePolicySelect}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
            {isDirtyFormDisplayed && (
              <DirtyFormAlert
                dirty={isDirty}
                closeAlert={() => setDirtyFormDisplay(false)}
                closeCb={closeCb}
              />
            )}
          </>
        )}
      </div>
    </SlidePane>
  );
};

SelectBundleForm.propTypes = {
  closeCb: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  bundleToEdit: PropTypes.object,
  title: PropTypes.string.isRequired,
  width: PropTypes.string,
};

SelectBundleForm.defaultProps = {
  bundleToEdit: null,
  width: '80%',
};

export default SelectBundleForm;
